import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Grid,
  TextField,
  Typography,
  Stack,
  Button,
  IconButton,
  Checkbox,
  LinearProgress,
  Step,
  StepLabel,
  Stepper,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CustomDialogTitle from "../../../components/Common/customDialogTitle";
import useAxiosInstance from "../../../hooks/useAxiosInstance";
import { API_RESULT, API_VERSION } from "../../../enums/common.enum";
import { errorToast, handleError, successToast } from "../../../utils/common";
import { useParams } from "react-router-dom";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

const today = new Date();
today.setHours(0, 0, 0, 0);

const startOfPreviousMonth = new Date(
  today.getFullYear(),
  today.getMonth() - 1,
  1,
);

const formatDateTime = (date = new Date()) => {
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const validationSchema = Yup.object({
  invoice_id: Yup.string().required("Invoice ID is required"),
  payee_name: Yup.string().required("Payee name is required"),
  invoice_date: Yup.date()
    .required("Invoice date is required")
    .min(
      startOfPreviousMonth,
      "Invoice date cannot be older than previous month",
    ),
  due_date: Yup.date()
    .required("Due date is required")
    .min(today, "Due date cannot be before today"),
  invoice_amount: Yup.number()
    .typeError("Enter valid amount")
    .positive("Amount must be greater than 0")
    .required("Invoice amount is required"),
  payable_amount: Yup.number()
    .typeError("Enter valid amount")
    .max(
      Yup.ref("invoice_amount"),
      "Payable amount cannot exceed invoice amount",
    )
    .required("Payable amount is required"),
  // tenure: Yup.string().required("Please select EMI tenure"),
  beneficiary_account_name: Yup.string().when("disbursement_type", {
    is: "third_party",
    then: (schema) => schema.required("Account name is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  beneficiary_bank_name: Yup.string().when("disbursement_type", {
    is: "third_party",
    then: (schema) => schema.required("Bank name is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  beneficiary_account_no: Yup.string().when("disbursement_type", {
    is: "third_party",
    then: (schema) => schema.required("Account number is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  beneficiary_ifsc: Yup.string().when("disbursement_type", {
    is: "third_party",
    then: (schema) => schema.required("IFSC code is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

type UploadItem = {
  id: string;
  file: File;
  progress: number;
  uploading: boolean;
  url?: string;
};

export const AddInvoiceModal = ({
  borrowerInfo,
  setData,
  open,
  onClose,
}: any) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const privateInstance = useAxiosInstance();

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const params = useParams();
  const activeBorrowerId = params?.borrowerId;
  const [fileUrls, setFileUrls] = useState<string[]>([]);
  const [files, setFiles] = useState<UploadItem[]>([]);
  const [activeStep, setActiveStep] = useState<0 | 1 | 2>(0);

  const steps = ["Upload Invoice", "Invoice Details", "Account Details"];

  const initialValues = {
    invoice_id: "",
    payee_name: "",
    invoice_date: "",
    due_date: "",
    invoice_amount: "",
    payable_amount: "",
    // tenure: "7",
    rate_of_interest: "",
    repayment_tenure: "",
    beneficiary_bank_name: "",
    beneficiary_account_name: "",
    beneficiary_account_no: "",
    beneficiary_ifsc: "",
    consent: true,
  };

  useEffect(() => {
    if (!open) {
      setFileUrls([]);
      setFiles([]);
      setUploadProgress(0);
      setUploading(false);
      setActiveStep(0);
    }
  }, [open]);

  const primaryBank =
    borrowerInfo?.bank_details?.find((b: any) => b.is_primary) ||
    borrowerInfo?.bank_details?.[0];

  const isSelfDisbursement =
    borrowerInfo?.credit_details?.disbursement_type === "self";

  const isUploadComplete =
    files.length > 0 && files.every((f) => f.progress === 100);

  const isSameInvoice = (a: any, b: any) => {
    return (
      a.invoice_id === b.invoice_id &&
      a.payee_name === b.payee_name &&
      a.invoice_date === b.invoice_date &&
      a.invoice_due_date === b.invoice_due_date &&
      Number(a.invoice_amount) === Number(b.invoice_amount) &&
      Number(a.payable_amount) === Number(b.payable_amount)
    );
  };

  const handleUpload = async (file: File) => {
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      errorToast("Max allowed file size is 10 MB");
      return;
    }

    const id = crypto.randomUUID();

    setFiles((prev) => [
      ...prev,
      {
        id,
        file,
        progress: 0,
        uploading: true,
      },
    ]);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await privateInstance.post(
        `${API_VERSION.V1}/orders/reports/upload?report_type=invoice`,
        formData,
        {
          onUploadProgress: (e) => {
            if (e.total) {
              const percent = Math.round((e.loaded * 100) / e.total);

              // ✅ UPDATE FILE PROGRESS
              setFiles((prev) =>
                prev.map((f) =>
                  f.id === id ? { ...f, progress: percent } : f,
                ),
              );
            }
          },
        },
      );

      if (res?.data?.status === API_RESULT.SUCCESS) {
        const uploadedUrl = res.data.response?.url;

        setFiles((prev) =>
          prev.map((f) =>
            f.id === id
              ? {
                  ...f,
                  progress: 100,
                  uploading: false,
                  url: uploadedUrl,
                }
              : f,
          ),
        );

        setFileUrls((prev) => [...prev, uploadedUrl]);

        successToast("File uploaded successfully");
      } else {
        errorToast(res.data.message);
      }
    } catch (err) {
      handleError(err);
      setFiles((prev) =>
        prev.map((f) => (f.id === id ? { ...f, uploading: false } : f)),
      );
    }
  };

  const handleSubmit = async (values: any, actions: any) => {
    if (fileUrls.length === 0) {
      errorToast("Please upload at least one invoice");
      actions.setSubmitting(false);
      return;
    }

    const beneficiaryDetails = isSelfDisbursement
      ? {
          beneficiary_account_name: primaryBank?.account_name || "",
          beneficiary_bank_name: primaryBank?.bank || "",
          beneficiary_account_no: primaryBank?.account_no || "",
          beneficiary_ifsc_code: primaryBank?.ifsc_code || "",
        }
      : {
          beneficiary_account_name: values.beneficiary_account_name,
          beneficiary_bank_name: values.beneficiary_bank_name,
          beneficiary_account_no: values.beneficiary_account_no,
          beneficiary_ifsc_code: values.beneficiary_ifsc,
        };

    try {
      const payload = {
        borrower_id: activeBorrowerId,
        status: "under_review",
        invoice_id: values.invoice_id,
        payee_name: values.payee_name,
        invoice_date: values.invoice_date,
        invoice_due_date: values.due_date,
        invoice_amount: values.invoice_amount,
        payable_amount: values.payable_amount,
        rate_of_interest: borrowerInfo.credit_details.interest_rate,
        repayment_tenure: borrowerInfo.credit_details.emi_repayment_mode,
        invoice_file_url: fileUrls,
        disbursement_type: borrowerInfo?.credit_details?.disbursement_type,
        beneficiary_details: beneficiaryDetails,
        invoice_added_at: formatDateTime(),
      };

      const res = await privateInstance.post(
        `${API_VERSION.V3}/borrowers/applications`,
        payload,
      );

      if (res?.data?.status === API_RESULT.SUCCESS) {
        const newInvoice = res.data.response;

        setData((prev: any[]) => {
          const index = prev.findIndex((item) =>
            isSameInvoice(item, newInvoice),
          );

          return index !== -1
            ? prev.map((item, i) =>
                i === index ? { ...item, ...newInvoice } : item,
              )
            : [newInvoice, ...prev];
        });

        successToast("Invoice added successfully");
        onClose();
      } else {
        errorToast(res.data.message);
      }
    } catch (err) {
      handleError(err);
    } finally {
      actions.setSubmitting(false);
    }
  };

  const handleRemove = (id: string) => {
    setFiles((prevFiles) => {
      const fileToRemove = prevFiles.find((f) => f.id === id);
      if (fileToRemove?.url) {
        setFileUrls((prevUrls) =>
          prevUrls.filter((url) => url !== fileToRemove.url),
        );
      }
      return prevFiles.filter((f) => f.id !== id);
    });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files.length) {
      handleUpload(e.dataTransfer.files[0]);
    }
  };

  const getISTDateInput = (date = new Date()) => {
    const istOffset = 5.5 * 60 * 60 * 1000;
    const istDate = new Date(date.getTime() + istOffset);
    return istDate.toISOString().split("T")[0];
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <CustomDialogTitle id="customized-dialog-title" onClose={onClose}>
        Add Invoice
      </CustomDialogTitle>

      <Box mb={3}>
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{
            px: 2,
            "& .MuiStepLabel-label": {
              fontSize: 13,
              fontWeight: 500,
              color: "#6B7280", // gray-500
            },
            "& .MuiStepLabel-label.Mui-active": {
              color: "#2563EB", // blue-600
              fontWeight: 600,
            },
            "& .MuiStepLabel-label.Mui-completed": {
              color: "#16A34A", // green-600
            },
            "& .MuiStepIcon-root": {
              fontSize: 20,
              color: "#D1D5DB", // gray-300
            },
            "& .MuiStepIcon-root.Mui-active": {
              color: "#2563EB",
            },
            "& .MuiStepIcon-root.Mui-completed": {
              color: "#16A34A",
            },
            "& .MuiStepConnector-line": {
              borderColor: "#E5E7EB",
              borderTopWidth: 2,
            },
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            handleChange,
            setFieldValue,
            errors,
            touched,
            isSubmitting,
          }) => {
            const isInvoiceDetailsValid =
              values.invoice_id &&
              values.payee_name &&
              values.invoice_date &&
              values.due_date &&
              values.invoice_amount &&
              values.payable_amount &&
              !errors.invoice_id &&
              !errors.payee_name &&
              !errors.invoice_date &&
              !errors.due_date &&
              !errors.invoice_amount &&
              !errors.payable_amount;

            const isBeneficiaryDetailsValid =
              borrowerInfo?.credit_details?.disbursement_type === "self" ||
              (values.beneficiary_account_name &&
                values.beneficiary_account_no &&
                values.beneficiary_bank_name &&
                values.beneficiary_ifsc &&
                !errors.beneficiary_account_name &&
                !errors.beneficiary_account_no &&
                !errors.beneficiary_bank_name &&
                !errors.beneficiary_ifsc);
            return (
              <Form>
                <Stack spacing={2}>
                  {activeStep === 0 && (
                    <>
                      <Box
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={handleDrop}
                        onClick={() => inputRef.current?.click()}
                        sx={{
                          border: "2px dashed #dcdcdc",
                          borderRadius: 2,
                          p: 4,
                          textAlign: "center",
                          cursor: "pointer",
                          backgroundColor: "#fff",
                        }}
                      >
                        <CloudUploadOutlinedIcon
                          sx={{ fontSize: 36, color: "#9e9e9e" }}
                        />

                        <Typography fontWeight={500} mt={1}>
                          Click to upload{" "}
                          <span style={{ color: "#888" }}>
                            or drag and drop
                          </span>
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                          Upload .txt, .docx, PDF (MAX. file size 20MB)
                        </Typography>

                        <input
                          ref={inputRef}
                          hidden
                          multiple
                          type="file"
                          accept=".txt,.docx,application/pdf,image/*"
                          onChange={(e) =>
                            e.target.files && handleUpload(e.target.files[0])
                          }
                        />
                      </Box>

                      <Box mt={1}>
                        {files.map((item) => (
                          <Box key={item.id} mb={1.5}>
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="space-between"
                              sx={{
                                border: "1px solid #e0e0e0",
                                borderRadius: 2,
                                px: 2,
                                py: 1.5,
                                backgroundColor: "#fafafa",
                              }}
                            >
                              <Box display="flex" alignItems="center" gap={1}>
                                <InsertDriveFileOutlinedIcon color="action" />
                                <Typography fontSize={14}>
                                  {item.file.name}
                                </Typography>
                              </Box>

                              <Box display="flex" alignItems="center" gap={0.5}>
                                {/* ✅ Show View button ONLY when upload is complete */}
                                {item.progress === 100 && (
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      window.open(
                                        URL.createObjectURL(item.file),
                                        "_blank",
                                      )
                                    }
                                  >
                                    <VisibilityOutlinedIcon fontSize="small" />
                                  </IconButton>
                                )}

                                <IconButton
                                  size="small"
                                  onClick={() => handleRemove(item.id)}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>

                            {item.progress < 100 && (
                              <>
                                <LinearProgress
                                  variant="determinate"
                                  value={item.progress}
                                  sx={{
                                    mt: 1,
                                    height: 6,
                                    borderRadius: 3,
                                  }}
                                />

                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                  textAlign="right"
                                  mt={0.5}
                                >
                                  {item.progress}%
                                </Typography>
                              </>
                            )}
                          </Box>
                        ))}
                      </Box>
                    </>
                  )}

                  {activeStep === 1 && (
                    <>
                      <Box>
                        <Grid container spacing={2}>
                          <Grid item xs={4}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Invoice ID"
                              name="invoice_id"
                              value={values.invoice_id}
                              onChange={handleChange}
                              error={
                                !!touched.invoice_id && !!errors.invoice_id
                              }
                              helperText={
                                touched.invoice_id && errors.invoice_id
                              }
                            />
                          </Grid>

                          <Grid item xs={8}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Payee Name"
                              name="payee_name"
                              value={values.payee_name}
                              onChange={handleChange}
                              error={
                                !!touched.payee_name && !!errors.payee_name
                              }
                              helperText={
                                touched.payee_name && errors.payee_name
                              }
                            />
                          </Grid>

                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              size="small"
                              type="date"
                              label="Invoice Date"
                              name="invoice_date"
                              InputLabelProps={{ shrink: true }}
                              value={values.invoice_date}
                              onChange={handleChange}
                              inputProps={{
                                min: startOfPreviousMonth
                                  .toISOString()
                                  .split("T")[0],
                              }}
                              error={
                                !!touched.invoice_date && !!errors.invoice_date
                              }
                              helperText={
                                touched.invoice_date && errors.invoice_date
                              }
                            />
                          </Grid>

                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              size="small"
                              type="date"
                              label="Invoice Due Date"
                              name="due_date"
                              inputProps={{
                                min: getISTDateInput(),
                              }}
                              InputLabelProps={{ shrink: true }}
                              value={values.due_date}
                              onChange={handleChange}
                              error={!!touched.due_date && !!errors.due_date}
                              helperText={touched.due_date && errors.due_date}
                            />
                          </Grid>

                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Invoice Amount (₹)"
                              name="invoice_amount"
                              value={values.invoice_amount}
                              onChange={handleChange}
                              error={
                                !!touched.invoice_amount &&
                                !!errors.invoice_amount
                              }
                              helperText={
                                touched.invoice_amount && errors.invoice_amount
                              }
                            />
                          </Grid>

                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Payable Amount (₹)"
                              name="payable_amount"
                              value={values.payable_amount}
                              onChange={handleChange}
                              error={
                                !!touched.payable_amount &&
                                !!errors.payable_amount
                              }
                              helperText={
                                touched.payable_amount && errors.payable_amount
                              }
                            />
                          </Grid>

                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              size="small"
                              name="rate_of_interest"
                              label="Interest Rate (%)"
                              value={
                                borrowerInfo?.credit_details?.interest_rate !=
                                null
                                  ? `${borrowerInfo.credit_details.interest_rate}%`
                                  : "--"
                              }
                              disabled
                            />
                          </Grid>

                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              size="small"
                              name="repayment_tenure"
                              label="Repayment Tenure (Days)"
                              value={
                                borrowerInfo?.credit_details?.emi_repayment_mode
                                  ? borrowerInfo.credit_details
                                      .emi_repayment_mode
                                  : "--"
                              }
                              disabled
                            />
                          </Grid>
                        </Grid>

                        <Stack
                          sx={{
                            mt: 2,
                          }}
                        >
                          <Typography variant="body2" fontWeight={600}>
                            Note:
                          </Typography>
                          <Typography variant="body2">
                            1. <b>Invoice Amount</b> is the total bill amount.
                          </Typography>
                          <Typography variant="body2">
                            2. <b>Payable Amount</b> is the amount that will be
                            paid to the distributor from MedPay credit.
                          </Typography>
                        </Stack>
                      </Box>
                    </>
                  )}

                  {activeStep === 2 && (
                    <>
                      <Box>
                        <Grid container spacing={2}>
                          {borrowerInfo?.credit_details?.disbursement_type && (
                            <>
                              <Grid item xs={12}>
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  color="secondary"
                                >
                                  Beneficiary Account Details
                                </Typography>
                              </Grid>

                              <Grid item xs={6}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  name="beneficiary_account_name"
                                  label="Account Holder Name"
                                  value={
                                    borrowerInfo?.credit_details
                                      ?.disbursement_type === "self"
                                      ? primaryBank?.account_name || "--"
                                      : values.beneficiary_account_name
                                  }
                                  onChange={(e) =>
                                    setFieldValue(
                                      "beneficiary_account_name",
                                      e.target.value,
                                    )
                                  }
                                  disabled={
                                    borrowerInfo?.credit_details
                                      ?.disbursement_type === "self"
                                  }
                                  InputLabelProps={{
                                    required:
                                      borrowerInfo?.credit_details
                                        ?.disbursement_type !== "self",
                                  }}
                                />
                              </Grid>

                              <Grid item xs={6}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  name="beneficiary_bank_name"
                                  label="Bank Name"
                                  value={
                                    borrowerInfo?.credit_details
                                      ?.disbursement_type === "self"
                                      ? primaryBank?.bank || "--"
                                      : values.beneficiary_bank_name
                                  }
                                  onChange={(e) =>
                                    setFieldValue(
                                      "beneficiary_bank_name",
                                      e.target.value,
                                    )
                                  }
                                  disabled={
                                    borrowerInfo?.credit_details
                                      ?.disbursement_type === "self"
                                  }
                                  InputLabelProps={{
                                    required:
                                      borrowerInfo?.credit_details
                                        ?.disbursement_type !== "self",
                                  }}
                                />
                              </Grid>

                              <Grid item xs={6}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  name="beneficiary_account_no"
                                  label="Account Number"
                                  value={
                                    borrowerInfo?.credit_details
                                      ?.disbursement_type === "self"
                                      ? primaryBank?.account_no || "--"
                                      : values.beneficiary_account_no
                                  }
                                  onChange={(e) =>
                                    setFieldValue(
                                      "beneficiary_account_no",
                                      e.target.value,
                                    )
                                  }
                                  disabled={
                                    borrowerInfo?.credit_details
                                      ?.disbursement_type === "self"
                                  }
                                  InputLabelProps={{
                                    required:
                                      borrowerInfo?.credit_details
                                        ?.disbursement_type !== "self",
                                  }}
                                />
                              </Grid>

                              <Grid item xs={6}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  name="beneficiary_ifsc"
                                  label="IFSC Code"
                                  value={
                                    borrowerInfo?.credit_details
                                      ?.disbursement_type === "self"
                                      ? primaryBank?.ifsc_code || "--"
                                      : values.beneficiary_ifsc
                                  }
                                  onChange={(e) =>
                                    setFieldValue(
                                      "beneficiary_ifsc",
                                      e.target.value,
                                    )
                                  }
                                  disabled={
                                    borrowerInfo?.credit_details
                                      ?.disbursement_type === "self"
                                  }
                                  InputLabelProps={{
                                    required:
                                      borrowerInfo?.credit_details
                                        ?.disbursement_type !== "self",
                                  }}
                                />
                              </Grid>
                            </>
                          )}
                        </Grid>

                        <Box
                          sx={{
                            border: "1px solid #E5E7EB",
                            borderRadius: 2,
                            p: 2,
                            mt: 2,
                            backgroundColor: "#FAFAFA",
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="flex-start"
                          >
                            <Checkbox
                              checked
                              disabled
                              sx={{
                                mt: 0.5,
                                color: "#2563EB",
                                "&.Mui-checked": {
                                  color: "#2563EB",
                                },
                              }}
                            />

                            <Stack spacing={0.5}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                I confirm the details are accurate and approve
                                the disbursement to the mentioned account.
                              </Typography>
                            </Stack>
                          </Stack>
                        </Box>
                      </Box>
                    </>
                  )}

                  {/* <Box>
                    <Typography fontWeight={600} mb={1}>
                      Select EMI Tenure
                    </Typography>
                    <EmiTenureSelector
                      value={values.tenure}
                      onChange={(val: string) => setFieldValue("tenure", val)}
                    />
                  </Box> */}

                  <Box display="flex" justifyContent="center" gap={2}>
                    {activeStep === 1 && (
                      <Button
                        variant="outlined"
                        onClick={() => setActiveStep(0)}
                      >
                        Back
                      </Button>
                    )}

                    {activeStep < 2 ? (
                      <Button
                        variant="contained"
                        disabled={
                          (activeStep === 0 && !isUploadComplete) ||
                          (activeStep === 1 && !isInvoiceDetailsValid)
                        }
                        onClick={() =>
                          setActiveStep((prev) => (prev + 1) as any)
                        }
                      >
                        Next
                      </Button>
                    ) : (
                      <LoadingButton
                        type="submit"
                        variant="contained"
                        disabled={!isBeneficiaryDetailsValid || isSubmitting}
                        loading={isSubmitting}
                      >
                        Add Invoice
                      </LoadingButton>
                    )}
                  </Box>
                </Stack>
              </Form>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};
